/**
 * Hooks de dados da mensageria interna dirigida entre operadores (Item 8 / #8b).
 *
 * Consome o backend real (apps/engine/src/modules/peer-coordination/direct-messages):
 *   - GET  /v1/direct-messages/threads · threads/:id · unread-count · direct-message-participate
 *   - POST /v1/direct-messages/threads · broadcast · direct-message-initiate
 *   - POST /v1/direct-messages/threads/:id/messages · /read · direct-message-participate
 *
 * Queries GATEADAS por capability no `enabled` (não só no render) — sem a cap o
 * fetch nem dispara (evita 403 vazando em toast; lição "gatear o fetch"). Mutações
 * invalidam a lista de threads + o badge de não-lido (e o thread específico quando
 * aplicável). A entrega ao vivo (WS-delta) entra numa fatia posterior.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../../hooks/use-capability';
import { api } from '../../../lib/api-client';

import type {
  BroadcastBody,
  BroadcastResult,
  InitiateThreadBody,
  InitiateThreadResult,
  MarkReadResult,
  PostMessageBody,
  PostMessageResult,
  RecipientScope,
  ThreadDetail,
  ThreadSummary,
} from '../types';
import type { UseMutationResult } from '@tanstack/react-query';

export const ROOT_KEY = 'direct-messages';
export const THREADS_KEY = [ROOT_KEY, 'threads'] as const;
export const UNREAD_KEY = [ROOT_KEY, 'unread'] as const;
export const RECIPIENT_SCOPE_KEY = [ROOT_KEY, 'recipient-scope'] as const;
export const threadKey = (threadId: string): readonly [string, string, string] => [
  ROOT_KEY,
  'thread',
  threadId,
];

const STALE = 30_000;

export function useThreads(): {
  data: ThreadSummary[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const canRead = useCapability('direct-message-participate');
  const query = useQuery({
    queryKey: THREADS_KEY,
    queryFn: () => api.listDirectThreads(),
    enabled: canRead,
    staleTime: STALE,
  });
  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

export function useThread(threadId: string | null): {
  data: ThreadDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const canRead = useCapability('direct-message-participate');
  const query = useQuery({
    queryKey: threadKey(threadId ?? ''),
    queryFn: () => api.getDirectThread(threadId!),
    enabled: canRead && !!threadId,
    staleTime: STALE,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

export function useUnreadCount(): {
  unread: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const canRead = useCapability('direct-message-participate');
  const query = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => api.getDirectUnreadCount(),
    enabled: canRead,
    staleTime: STALE,
  });
  return {
    unread: query.data?.unread ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

const EMPTY_PEOPLE: RecipientScope['people'] = [];
const EMPTY_TEAMS: RecipientScope['teams'] = [];

/**
 * useRecipientScope — diretório de destinatários que o ator pode INICIAR (picker
 * escopado). Gateada no `enabled` por QUALQUER cap de iniciação (sem elas o fetch
 * nem dispara — "gatear o fetch, não só o render"; e o backend 403aria). Devolve
 * `people`/`teams` (com nomes, autossuficientes para o picker), mais `direction`
 * para a copy honesta e `isLoading` para evitar o flash de "nenhuma pessoa". Os
 * arrays vêm direto de `query.data` (ref estável do React Query entre renders).
 */
export function useRecipientScope(): {
  people: RecipientScope['people'];
  teams: RecipientScope['teams'];
  direction: RecipientScope['direction'];
  isLoading: boolean;
  canInitiate: boolean;
} {
  const canDown = useCapability('direct-message-initiate');
  const canUp = useCapability('direct-message-initiate-upward');
  // Mensageria lateral/peer (opt-in) também habilita a busca de destinatários.
  const canLateral = useCapability('direct-message-initiate-lateral');
  const canAnyone = useCapability('direct-message-initiate-anyone');
  const canInitiate = canDown || canUp || canLateral || canAnyone;
  const query = useQuery({
    queryKey: RECIPIENT_SCOPE_KEY,
    queryFn: () => api.getDirectRecipientScope(),
    enabled: canInitiate,
    staleTime: STALE,
  });
  return {
    people: query.data?.people ?? EMPTY_PEOPLE,
    teams: query.data?.teams ?? EMPTY_TEAMS,
    direction:
      query.data?.direction ?? (canDown && canUp ? 'both' : canDown ? 'down' : canUp ? 'up' : 'none'),
    isLoading: canInitiate && query.isLoading,
    canInitiate,
  };
}

export function useInitiateThread(): UseMutationResult<
  InitiateThreadResult,
  unknown,
  InitiateThreadBody
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InitiateThreadBody) => api.initiateDirectThread(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: THREADS_KEY });
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });
}

export function useBroadcast(): UseMutationResult<BroadcastResult, unknown, BroadcastBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BroadcastBody) => api.broadcastDirectMessage(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: THREADS_KEY });
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });
}

export function useReply(
  threadId: string,
): UseMutationResult<PostMessageResult, unknown, PostMessageBody> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PostMessageBody) => api.postDirectMessage(threadId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: THREADS_KEY });
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
    },
  });
}

export function useMarkRead(): UseMutationResult<MarkReadResult, unknown, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) => api.markDirectThreadRead(threadId),
    onSuccess: (_data, threadId) => {
      void qc.invalidateQueries({ queryKey: THREADS_KEY });
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: threadKey(threadId) });
    },
  });
}
